// Output-files derivation for the global Layer Properties console.
//
// Surfaces the converter output files + derived files a layer references: the
// primary artifact, PMTiles archive, GLBs, COGs, tileset.json, terrain
// layer.json, Potree/COPC metadata, plus sidecars (QC manifest, tilejson,
// metadata, provenance) — whenever they are discoverable from the layer,
// its style/manifest, or its endpoints. Safe with missing metadata: returns an
// empty list and `hasConverterMetadata: false` rather than guessing URLs that
// might 404.

import type { Layer } from '@geosoft/viewer-sdk';
import { asRecord, intakeManifest, styleRecord } from './source';
import type {
  LayerOutputFilesDiagnostics,
  OutputFileRef,
  OutputFileRole,
} from './types';

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

/** Extract the embedded COG file URL from a TiTiler `?url=` tile template. */
export function extractCogFileUrl(tilesTemplate: string | undefined): string | null {
  if (!tilesTemplate) return null;
  const match = /[?&]url=([^&]+)/.exec(tilesTemplate);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

export function buildOutputFilesDiagnostics(layer: Layer): LayerOutputFilesDiagnostics {
  const seen = new Set<string>();
  const files: OutputFileRef[] = [];
  const style = styleRecord(layer) ?? {};
  const manifest = intakeManifest(layer) ?? {};

  const add = (
    role: OutputFileRole,
    label: string,
    url: string | null,
    derived = false,
  ) => {
    if (!url) return;
    const dedupeKey = `${role}:${url}`;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    files.push({ role, label, url, derived });
  };

  const endpoints = layer.endpoints ?? {};

  // Primary payloads by endpoint.
  add('pmtiles-archive', 'PMTiles archive', str(endpoints.pmtiles));
  add('glb', 'glTF / GLB model', str(endpoints.model) ?? str(manifest['glbUrl']));
  add('tileset', '3D Tiles tileset.json', str(endpoints.tileset3d) ?? str(manifest['tilesetUrl']));
  add('terrain-layer-json', 'Terrain layer.json', str(manifest['layerJsonUrl']));
  add('copc', 'COPC point cloud', str(endpoints.copc));
  add(
    'potree-metadata',
    'Potree metadata.json',
    str(endpoints.potree) ?? (str(manifest['metadataUrl']) && layer.kind === 'pointcloud' ? str(manifest['metadataUrl']) : null),
  );

  // COG file (extracted from the TiTiler tile template).
  add('cog', 'COG / GeoTIFF', extractCogFileUrl(endpoints.tiles));

  // GeoJSON / features payload.
  if (layer.kind === 'vector' && !endpoints.pmtiles) {
    add('primary', 'GeoJSON / features', str(endpoints.features));
  }

  // Sidecars / metadata. These also drive `hasConverterMetadata`.
  const metadataUrl = str(style['metadataUrl']) ?? str(manifest['metadataUrl']);
  const qcManifestUrl =
    str(style['qcManifestUrl']) ??
    str(manifest['qcManifestUrl']) ??
    str(manifest['qcUrl']);
  const tilejsonUrl = str(style['tilejsonUrl']) ?? str(manifest['tilejsonUrl']);
  const provenanceUrl =
    str(layer.crsProvenanceUrl) ?? str(style['provenanceUrl']) ?? str(manifest['provenanceUrl']);
  const manifestSidecar = str(style['manifestUrl']) ?? str(manifest['manifestUrl']);

  add('qc-manifest', 'QC manifest', qcManifestUrl);
  add('tilejson', 'TileJSON', tilejsonUrl);
  add('metadata', 'Metadata', metadataUrl);
  add('metadata', 'Manifest sidecar', manifestSidecar);
  add('provenance', 'Provenance / CRS transform', provenanceUrl);

  const hasConverterMetadata = Boolean(
    qcManifestUrl || provenanceUrl || metadataUrl || manifestSidecar || asRecord(style['intakeManifest']),
  );

  return { files, hasConverterMetadata };
}
